'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { getNotificationsAction } from '../actions/notification.actions'
import type { AppNotificationPage } from '../types/notification.types'
import { queryCachePolicy } from '@/shared/state/query-cache-policy'

export const notificationsQueryKey = ['notifications', 'list'] as const

/** Mobile pages 15 at a time; the web mirrors that page size. */
export const NOTIFICATIONS_PAGE_SIZE = 15

/**
 * Decides whether to ask for another page, and from where.
 *
 * This used to be `lastPage.length < PAGE_SIZE ? undefined : …` — stop as soon
 * as a page comes back short. That inference is wrong against this endpoint:
 * the server drops rows it can't render, so a nowhere-near-finished inbox
 * routinely answered a request for 15 with 7. Measured on api-dev against an
 * account with 354 unread, page one held 7 items and the first page of 1 came
 * back empty outright — so infinite scroll stopped after page one and the
 * other 347 notifications were unreachable (PB-531).
 *
 * The server now states whether more exist and which raw offset to resume from,
 * and this reads that answer rather than guessing from a length. Exported so it
 * can be tested directly: the failure mode is invisible in the UI, because a
 * frozen list looks exactly like an inbox that really does hold 7 things.
 */
export function nextNotificationsPageParam(
  lastPage: AppNotificationPage,
  _allPages: AppNotificationPage[],
  lastPageParam: number
): number | undefined {
  if (!lastPage.hasMore) return undefined
  // Never hand back a cursor that doesn't advance — react-query would refetch
  // the same page forever.
  if (lastPage.nextSkip <= lastPageParam) return undefined
  return lastPage.nextSkip
}

export function useNotifications(options?: {
  /** The first page, rendered on the server for instant paint. */
  initialData?: AppNotificationPage
  enabled?: boolean
}) {
  return useInfiniteQuery({
    queryKey: notificationsQueryKey,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const res = await getNotificationsAction(pageParam, NOTIFICATIONS_PAGE_SIZE)
      if (!res.success) throw new Error(res.error.userMessage)
      return res.data
    },
    initialPageParam: 0,
    getNextPageParam: nextNotificationsPageParam,
    enabled: options?.enabled ?? true,
    initialData: options?.initialData
      ? { pages: [options.initialData], pageParams: [0] }
      : undefined,
    // Opt into focus refetching (the global QueryClient default is `false`)
    // so returning to /alerts refreshes the list immediately, mirroring
    // mobile's app-resume refresh. `staleTime` debounces rapid focus flips.
    refetchOnWindowFocus: true,
    staleTime: queryCachePolicy.liveSummary.staleTime,
  })
}
